import { GitBranchIcon } from '@phosphor-icons/react'
import { Handle, Position, useNodeConnections, useNodeId, type NodeProps } from '@xyflow/react'
import { useListCopy } from 'app/hooks/useListCopy'
import { cn } from 'app/lib/utils'
import { conditionGroupText } from 'app/lib/workflows/conditions'
import { labelForToken } from 'app/lib/workflows/dataContext'
import type { BranchFlowNode } from 'app/types/workflows'
import { AppendButton } from './AppendButton'
import { NodeShell } from './NodeShell'

const HANDLE = `!h-2.5 !w-2.5 !border-2 !border-(--surface)`

/** Generic split on a yes/no condition (chosen in the inspector). Two source handles: `yes` and `no`. */
export function BranchNode({ data, selected }: NodeProps<BranchFlowNode>) {
  const nodeId = useNodeId()
  const copy = useListCopy()
  const yesOpen = useNodeConnections({ handleType: `source`, handleId: `yes` }).length === 0
  const noOpen = useNodeConnections({ handleType: `source`, handleId: `no` }).length === 0
  return (
    <NodeShell
      icon={GitBranchIcon}
      accent="violet"
      kind="Branch"
      label={data.label}
      // labelForToken already resolves a bare field key (no `contact.`/`activity.` prefix passes
      // through fieldKeyFromToken unchanged) and applies fieldDisplayLabel, so it reads "List
      // stage" once grouped-navigation is on instead of the raw catalog label.
      description={conditionGroupText(data, (key) => (key ? labelForToken(key, copy) : undefined))}
      selected={selected}
      hasSource={false}
    >
      <div className="flex justify-between px-5 pb-2 text-[10px] font-semibold">
        <span className="text-default">YES</span>
        <span className="text-minimal">NO</span>
      </div>
      <Handle
        id="yes"
        type="source"
        position={Position.Bottom}
        style={{ left: `26%` }}
        className={cn(HANDLE, `!bg-inverse`)}
      />
      <Handle
        id="no"
        type="source"
        position={Position.Bottom}
        style={{ left: `74%` }}
        className={cn(HANDLE, `!bg-inverse/40`)}
      />
      {nodeId != null && yesOpen && (
        <AppendButton nodeId={nodeId} sourceHandle="yes" className="-bottom-8 left-[26%] -translate-x-1/2" />
      )}
      {nodeId != null && noOpen && (
        <AppendButton nodeId={nodeId} sourceHandle="no" className="-bottom-8 left-[74%] -translate-x-1/2" />
      )}
    </NodeShell>
  )
}
